import { useEffect, useId, useRef } from 'react'
import { useInlineFieldSpeechRecognition } from '../hooks/useInlineFieldSpeechRecognition'
import { formatChatDateDisplay } from '../utils/formatChatDate'
import type { ReminderInlineVoiceTranscribeConfig } from './ReminderPreviewInlineEdit'

export type SecretaryMessageInlineEditField = 'purpose' | 'message' | 'recipient_name'

export type SecretaryMessageInlineEditProps = {
  field: SecretaryMessageInlineEditField
  fr: boolean
  draft: string
  saving: boolean
  error: string | null
  assigneeOptions?: Array<{ selection_id: string; label: string; dob: string; gender?: string }>
  voiceTranscribe?: ReminderInlineVoiceTranscribeConfig
  onDraftChange: (value: string) => void
  onSave: () => void
  onCancel: () => void
  onAssigneeOptionSelect?: (selectionId: string) => void
}

function genderDisplay(value: string | undefined): string {
  const s = (value || '').trim().toLowerCase()
  if (!s) return '—'
  if (s === '1' || s === 'm' || s === 'male' || s === 'homme') return 'Homme'
  if (s === '2' || s === 'f' || s === 'female' || s === 'femme') return 'Femme'
  return (value || '').trim()
}

function fieldUi(field: SecretaryMessageInlineEditField, fr: boolean) {
  if (field === 'purpose') {
    return {
      rowLabel: fr ? 'Objet' : 'Subject',
      editTitle: fr ? "Modifier l'objet" : 'Edit subject',
      micLabel: fr ? "Dicter l'objet" : 'Dictate subject',
    }
  }
  if (field === 'message') {
    return {
      rowLabel: fr ? 'Message' : 'Message',
      editTitle: fr ? 'Modifier le message' : 'Edit message',
      micLabel: fr ? 'Dicter le message' : 'Dictate message',
    }
  }
  return {
    rowLabel: fr ? 'Destinataire' : 'Recipient',
    editTitle: fr ? 'Modifier le nom du destinataire' : 'Edit recipient name',
    micLabel: fr ? 'Dicter le destinataire' : 'Dictate recipient name',
  }
}

export function SecretaryMessageInlineEditRow(props: SecretaryMessageInlineEditProps) {
  const {
    field,
    fr,
    draft,
    saving,
    error,
    assigneeOptions,
    voiceTranscribe,
    onDraftChange,
    onSave,
    onCancel,
    onAssigneeOptionSelect,
  } = props
  const inputId = useId()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const ui = fieldUi(field, fr)
  const { supported, status, statusHint, error: speechError, toggle } = useInlineFieldSpeechRecognition({
    websocketUrl: voiceTranscribe?.websocketUrl ?? '',
    apiBaseUrl: voiceTranscribe?.apiBaseUrl,
    transcribeMode: voiceTranscribe?.transcribeMode,
    getMedicalLinkCredentials: voiceTranscribe?.getMedicalLinkCredentials,
    clientId: voiceTranscribe?.clientId,
    tenantId: voiceTranscribe?.tenantId,
    sessionId: voiceTranscribe?.sessionId,
    languageHint: fr ? 'fr' : 'en',
    disabled: saving,
    blocked: voiceTranscribe?.blocked,
    onBusyChange: voiceTranscribe?.onBusyChange,
    onTranscript: (text) => onDraftChange(text),
  })

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const helper =
    error ||
    speechError ||
    (status === 'listening' && statusHint.trim()
      ? statusHint.trim()
      : fr
        ? 'Appuyez sur le micro pour parler ou saisissez votre texte.'
        : 'Tap mic to speak or type to edit.')
  const listeningLabel = fr ? 'Ecoute en cours' : 'Listening'
  const micLabel = status === 'listening' ? listeningLabel : ui.micLabel

  return (
    <div className="vnw-secretaryMessagePreviewRow vnw-secretaryMessagePreviewRow--inline">
      <span className="vnw-secretaryMessagePreviewLabel">{ui.rowLabel}</span>
      <div className="vnw-reminderInlineEditBody vnw-secretaryInlineEditBody">
        <label className="vnw-reminderInlineEditLabel" htmlFor={inputId}>
          {ui.editTitle}
        </label>
        <div className="vnw-secretaryInlineFieldStack">
          <div className="vnw-reminderInlineEditInputWrap">
            <textarea
              id={inputId}
              ref={inputRef}
              className={
                'vnw-reminderInlineEditInput vnw-secretaryInlineEditInput' +
                (field === 'recipient_name' ? ' vnw-secretaryInlineEditInput--recipient' : '')
              }
              value={draft}
              disabled={saving}
              autoComplete="off"
              rows={field === 'message' ? 2 : 1}
              wrap={field === 'recipient_name' ? 'off' : 'soft'}
              onChange={(e) => onDraftChange(e.target.value)}
              onKeyDown={(e) => {
                const saveShortcut = e.key === 'Enter' && (e.metaKey || e.ctrlKey || field !== 'message')
                if (saveShortcut) {
                  e.preventDefault()
                  onSave()
                }
                if (e.key === 'Escape') {
                  e.preventDefault()
                  onCancel()
                }
              }}
            />
            <button
              type="button"
              className={
                'vnw-reminderInlineEditMic' + (status === 'listening' ? ' vnw-reminderInlineEditMic--active' : '')
              }
              aria-label={micLabel}
              title={supported ? micLabel : fr ? 'Saisie vocale indisponible' : 'Voice input unavailable'}
              disabled={saving || !supported}
              aria-pressed={status === 'listening'}
              onClick={() => toggle()}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
                <path
                  fill="currentColor"
                  d="M12 14a3 3 0 0 0 3-3V5a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.93V21h2v-3.07A7 7 0 0 0 19 11h-2Z"
                />
              </svg>
            </button>
          </div>
          {field === 'recipient_name' && assigneeOptions && assigneeOptions.length > 0 ? (
            <div className="vnw-secretaryInlineDropdown" role="listbox" aria-label={fr ? 'Choisir un destinataire' : 'Choose recipient'}>
              {assigneeOptions.map((opt) => (
                <button
                  key={opt.selection_id}
                  type="button"
                  className="vnw-secretaryInlineDropdownOption"
                  disabled={saving}
                  onClick={() => onAssigneeOptionSelect?.(opt.selection_id)}
                >
                  <span className="vnw-secretaryInlineDropdownPrimary">{opt.label}</span>
                  <span className="vnw-secretaryInlineDropdownMeta">
                    <span className="vnw-secretaryInlineDropdownMetaLabel">DOB</span>
                    <span>{formatChatDateDisplay(opt.dob) || '—'}</span>
                  </span>
                  <span className="vnw-secretaryInlineDropdownMeta">
                    <span className="vnw-secretaryInlineDropdownMetaLabel">{fr ? 'Genre' : 'Gender'}</span>
                    <span>{genderDisplay(opt.gender)}</span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <p className="vnw-reminderInlineEditHelper" role="status">
          {helper}
        </p>
        <div className="vnw-reminderInlineEditActions">
          <button
            type="button"
            className="vnw-reminderInlineEditBtn vnw-reminderInlineEditBtn--primary"
            disabled={saving || !draft.trim()}
            onClick={onSave}
          >
            {fr ? 'Enregistrer' : 'Save'}
          </button>
          <button
            type="button"
            className="vnw-reminderInlineEditBtn vnw-reminderInlineEditBtn--ghost"
            disabled={saving}
            onClick={onCancel}
          >
            {fr ? 'Annuler' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  )
}
